import { RootState, selectBundlePageIds, setViewportPageIdForRestorePageIndexInStore } from '@/store';
import { api } from '../core';
import { refreshBundleSectionsWithCurrentFilters } from './getBundleSections';

/** Walks outwards from `fromIndex` for the closest page the move leaves where it is. */
const findNearestStayingPageId = (
  bundlePageIds: string[],
  fromIndex: number,
  movedPageIds: Set<string>,
): string | undefined => {
  for (let offset = 0; offset < bundlePageIds.length; offset++) {
    const before = bundlePageIds[fromIndex - offset];
    if (before && !movedPageIds.has(before)) return before;

    const after = bundlePageIds[fromIndex + offset];
    if (after && !movedPageIds.has(after)) return after;
  }

  return undefined;
};

interface AddBundlePageCategoryResponse {
  response: boolean;
}

interface AddBundlePageCategoryProps {
  bundleId: string;
  providerSectionId: string;
  categoryId: string | null;
  categoryName: string | null;
  newCategoryIndex: number | null;
  subCategoryId: string | null;
  subCategoryName: string | null;
  newSubCategoryIndex: number | null;
  pageIds: string[];
  abortController?: AbortController;
}

const addBundlePageCategoryApi = api.injectEndpoints({
  endpoints: (builder) => ({
    addBundlePageCategory: builder.mutation<AddBundlePageCategoryResponse, AddBundlePageCategoryProps>({
      query: ({
        bundleId,
        providerSectionId,
        categoryId,
        categoryName,
        newCategoryIndex,
        subCategoryId,
        subCategoryName,
        newSubCategoryIndex,
        pageIds,
        abortController = new AbortController(),
      }: AddBundlePageCategoryProps) => ({
        url: '/bundle/category/add',
        method: 'POST',
        signal: abortController.signal,
        body: {
          bundle_id: bundleId,
          provider_section_id: providerSectionId,
          category_id: categoryId,
          category_name: categoryName,
          new_category_index: newCategoryIndex,
          sub_category_id: subCategoryId,
          sub_category_name: subCategoryName,
          new_sub_category_index: newSubCategoryIndex,
          page_ids: pageIds,
        },
      }),
      async onQueryStarted({ pageIds }, { dispatch, queryFulfilled, getState }) {
        try {
          const { data } = await queryFulfilled;
          const state = getState() as RootState;

          if (data) {
            // Anchoring the restore on the viewport page would drag the viewer into the
            // new section whenever that page is one of the moved ones, so anchor on the
            // nearest page that stays put instead. The success toast offers the jump.
            const anchorPageId = findNearestStayingPageId(
              selectBundlePageIds(state),
              state.bundleViewportData.viewportPageIndexInStore,
              new Set(pageIds),
            );

            if (anchorPageId) {
              dispatch(setViewportPageIdForRestorePageIndexInStore(anchorPageId));
            }
            refreshBundleSectionsWithCurrentFilters(dispatch, state);
          }
        } catch (error) {
          if (error instanceof Error && error.name === 'AbortError') {
            console.log('Request was aborted');
          } else {
            console.error('Error in addToLightBundle in onQueryStarted:', error);
          }
        }
      },
      extraOptions: { maxRetries: 0 },
    }),
  }),
});

/**
 * Export hooks for usage in functional components, which are
 * auto-generated based on the defined endpoints
 */
export const { useAddBundlePageCategoryMutation } = addBundlePageCategoryApi;
